import {
  buildCurve,
  curveKnots,
  curvePoint,
  curveBearing,
  pointAt,
  lowerIndex,
  evaluate,
  type Cubic,
  type Curve,
} from './tourCurve';
import { BOAT, CAMERA, PLAYER, WORLD } from '../core/config';
import { clamp } from '../core/math';
import type { InputFrame, Pose } from '../controls/types';
import type { Vec2, Vec3 } from '../geo/types';
import type { TourId, TourPlayer, TourRoute, TourSnapshot } from './types';
import { advanceLoop } from './tourLoop';

const SAMPLES_PER_SEGMENT = 96;
const SPEED_RAMP_SECONDS = 3;
const EPSILON = 1e-8;
const TAU = Math.PI * 2;
type GroundHeight = (p: Vec2) => number;

interface PreparedRoute {
  readonly route: TourRoute;
  readonly positions: Curve;
  readonly targets: Curve;
  readonly parameters: Float64Array;
  readonly distances: Float64Array;
  readonly inverseTangents: Float64Array;
  readonly headings: Float64Array;
  readonly bearings: Float64Array;
  readonly length: number;
}

function routeError(route: TourRoute, detail: string): Error {
  return new Error(`Tour "${route.id}": ${detail}`);
}

function validateRoute(route: TourRoute): void {
  if (!['flyover', 'walk', 'boat', 'churches'].includes(route.id)) {
    throw routeError(route, 'use id "flyover", "walk", "boat", or "churches".');
  }
  if (!['fly', 'walk', 'boat'].includes(route.kind)) {
    throw routeError(route, 'use kind "fly", "walk", or "boat".');
  }
  if (!route.title.trim()) throw routeError(route, 'provide a title.');
  if (route.path !== undefined && route.path !== 'linear') {
    throw routeError(
      route,
      'omit path for a smooth curve, or use "linear" for a prepared polyline.'
    );
  }
  if (!Number.isFinite(route.duration) || route.duration <= 0) {
    throw routeError(route, 'duration must be a finite number greater than zero.');
  }
  if (route.positions.length < 2 || route.targets.length !== route.positions.length) {
    throw routeError(
      route,
      'positions and targets must have the same length, with at least two points.'
    );
  }
  for (const key of ['positions', 'targets'] as const) {
    route[key].forEach((point, index) => {
      if (point.length !== 3 || !point.every(Number.isFinite)) {
        throw routeError(route, `${key}[${index}] must contain three finite world coordinates.`);
      }
    });
    if (route.closed) {
      const first = pointAt(route[key], 0),
        last = pointAt(route[key], route[key].length - 1);
      if (first.some((value, i) => Math.abs(value - (last[i] ?? value)) > EPSILON))
        throw routeError(route, `closed ${key} must end at their starting point.`);
    }
  }
}

function cameraPosition(
  route: TourRoute,
  curve: Curve,
  parameter: number,
  ground: GroundHeight
): Vec3 {
  const p = curvePoint(curve, parameter);
  if (route.kind === 'walk') return [p[0], ground([p[0], -p[2]]) + PLAYER.eyeHeight, p[2]];
  if (route.kind === 'boat') return [p[0], WORLD.waterY + BOAT.eyeHeight, p[2]];
  return p;
}

function bearing(position: Vec3, target: Vec3, fallback: number): number {
  const dx = target[0] - position[0];
  const dz = target[2] - position[2];
  return Math.hypot(dx, dz) < EPSILON ? fallback : Math.atan2(-dx, -dz);
}

function unwrap(angle: number, reference: number): number {
  return angle + TAU * Math.round((reference - angle) / TAU);
}

/** Monotone inverse arc-length tangents keep velocity continuous between table samples. */
function inverseTangents(distances: Float64Array, parameters: Float64Array): Float64Array {
  const tangents = new Float64Array(distances.length);
  let previousSpan = 0;
  let previousSlope = 0;
  for (let i = 0; i < distances.length - 1; i++) {
    const span = (distances[i + 1] ?? 0) - (distances[i] ?? 0);
    const slope = span > EPSILON ? ((parameters[i + 1] ?? 0) - (parameters[i] ?? 0)) / span : 0;
    if (i === 0) tangents[i] = slope;
    else if (previousSlope > 0 && slope > 0) {
      const w1 = 2 * span + previousSpan;
      const w2 = span + 2 * previousSpan;
      tangents[i] = (w1 + w2) / (w1 / previousSlope + w2 / slope);
    } else tangents[i] = 0;
    tangents[i + 1] = slope;
    previousSpan = span;
    previousSlope = slope;
  }
  return tangents;
}

function prepareRoute(route: TourRoute, ground: GroundHeight): PreparedRoute {
  validateRoute(route);
  const points = route.positions.map((p): Vec3 => [p[0], route.kind === 'fly' ? p[1] : 0, p[2]]);
  const knots = curveKnots(points, route.path === 'linear');
  const positions = buildCurve(points, knots, route.path === 'linear', route.closed);
  const targets = buildCurve(route.targets, knots, false, route.closed);
  const perSegment = route.path === 'linear' ? 1 : SAMPLES_PER_SEGMENT;
  const samples = positions.segments.length * perSegment;
  const parameters = new Float64Array(samples + 1);
  const distances = new Float64Array(samples + 1);
  const headings = new Float64Array(samples + 1);
  const bearings = new Float64Array(samples + 1);
  let previous = cameraPosition(route, positions, 0, ground);
  let length = 0;
  let heading = bearing(previous, curvePoint(targets, 0), 0);
  let pathHeading = curveBearing(positions, 0, 0);
  for (let i = 0; i <= samples; i++) {
    const segment = Math.min(Math.floor(i / perSegment), positions.segments.length - 1);
    const from = knots[segment] ?? 0;
    const fraction = i / perSegment - segment;
    const parameter = from + ((knots[segment + 1] ?? 1) - from) * fraction;
    const current = cameraPosition(route, positions, parameter, ground);
    length += Math.hypot(
      current[0] - previous[0],
      current[1] - previous[1],
      current[2] - previous[2]
    );
    heading = unwrap(bearing(current, curvePoint(targets, parameter), heading), heading);
    pathHeading = unwrap(curveBearing(positions, parameter, pathHeading), pathHeading);
    distances[i] = length;
    parameters[i] = parameter;
    headings[i] = heading;
    bearings[i] = pathHeading;
    previous = current;
  }
  if (!Number.isFinite(length) || length < EPSILON) {
    throw routeError(route, 'the camera path must have a finite, nonzero length.');
  }
  return {
    route,
    positions,
    targets,
    parameters,
    distances,
    inverseTangents: inverseTangents(distances, parameters),
    headings,
    bearings,
    length,
  };
}

/** The integral of a cosine speed ramp, with zero speed at both endpoints. */
function distanceProgress(elapsed: number, duration: number): number {
  const ramp = Math.min(SPEED_RAMP_SECONDS, duration / 2);
  const rampDistance = (t: number) => (t - (ramp / Math.PI) * Math.sin((Math.PI * t) / ramp)) / 2;
  const length = duration - ramp;
  if (elapsed < ramp) return rampDistance(elapsed) / length;
  if (elapsed > duration - ramp) return 1 - rampDistance(duration - elapsed) / length;
  return (elapsed - ramp / 2) / length;
}

function pathParameter(prepared: PreparedRoute, progress: number): number {
  if (progress <= 0) return 0;
  if (progress >= 1) return 1;
  const distance = progress * prepared.length;
  const distances = prepared.distances;
  const low = lowerIndex(distances, distance);
  const high = low + 1;
  const from = distances[low] ?? 0;
  const span = (distances[high] ?? from) - from;
  const u0 = prepared.parameters[low] ?? 0;
  if (span <= EPSILON) return u0;
  const u1 = prepared.parameters[high] ?? u0;
  const m0 = (prepared.inverseTangents[low] ?? 0) * span;
  const m1 = (prepared.inverseTangents[high] ?? 0) * span;
  const coefficients: Cubic = [u0, m0, 3 * (u1 - u0) - 2 * m0 - m1, 2 * (u0 - u1) + m0 + m1];
  return evaluate(coefficients, (distance - from) / span);
}

function headingReference(
  prepared: PreparedRoute,
  parameter: number,
  headings = prepared.headings
): number {
  const low = lowerIndex(prepared.parameters, parameter);
  const from = headings[low] ?? 0;
  const to = headings[low + 1] ?? from;
  const u0 = prepared.parameters[low] ?? 0;
  const u1 = prepared.parameters[low + 1] ?? 1;
  return from + ((to - from) * (parameter - u0)) / (u1 - u0);
}

function routeView(
  prepared: PreparedRoute,
  elapsed: number,
  ground: GroundHeight
): Pick<Pose, 'position' | 'yaw' | 'pitch'> {
  const parameter = pathParameter(prepared, playbackProgress(prepared.route, elapsed));
  const position = cameraPosition(prepared.route, prepared.positions, parameter, ground);
  const target = curvePoint(prepared.targets, parameter);
  const reference = headingReference(prepared, parameter);
  const yaw = unwrap(bearing(position, target, reference), reference);
  const horizontal = Math.hypot(target[0] - position[0], target[2] - position[2]);
  const pitch = clamp(
    Math.atan2(target[1] - position[1], horizontal),
    -PLAYER.pitchLimit,
    PLAYER.pitchLimit
  );
  return { position, yaw, pitch };
}

function playbackProgress(route: TourRoute, elapsed: number): number {
  return route.closed ? elapsed / route.duration : distanceProgress(elapsed, route.duration);
}

function snapshot(
  prepared: PreparedRoute,
  elapsed: number,
  paused: boolean,
  rate: number
): TourSnapshot {
  const { id, title, kind, duration } = prepared.route;
  const parameter = pathParameter(prepared, playbackProgress(prepared.route, elapsed));
  const reference = headingReference(prepared, parameter, prepared.bearings);
  const bearing = unwrap(curveBearing(prepared.positions, parameter, reference), reference);
  const progress = playbackProgress(prepared.route, elapsed);
  const chapters = prepared.route.chapters ?? [];
  const chapter = [...chapters].reverse().find((stop) => stop.at <= progress) ?? null;
  return { id, title, kind, elapsed, duration, paused, rate, bearing, chapter };
}

export function createTourPlayer(
  routes: readonly TourRoute[],
  groundHeightAt: GroundHeight
): TourPlayer {
  const prepared = new Map<TourId, PreparedRoute>();
  for (const route of routes) {
    if (prepared.has(route.id)) throw routeError(route, 'route ids must be unique.');
    prepared.set(route.id, prepareRoute(route, groundHeightAt));
  }
  return new Playback(prepared, groundHeightAt);
}

class Playback implements TourPlayer {
  private active: PreparedRoute | null = null;
  private clock = 0;
  private paused = false;
  private rate = 1;
  private cycles = 0;
  private yawOffset = 0;
  private pitchOffset = 0;

  constructor(
    private readonly routes: ReadonlyMap<TourId, PreparedRoute>,
    private readonly ground: GroundHeight
  ) {}

  private pose(
    input: Pick<InputFrame, 'lookDx' | 'lookDy'> = { lookDx: 0, lookDy: 0 }
  ): Pose | null {
    if (!this.active) return null;
    const view = routeView(this.active, this.clock, this.ground);
    const winding = (this.active.headings.at(-1) ?? 0) - (this.active.headings[0] ?? 0);
    if (Number.isFinite(input.lookDx)) this.yawOffset -= input.lookDx * PLAYER.lookRadPerPx;
    const lookDy = Number.isFinite(input.lookDy) ? input.lookDy : 0;
    const pitch = clamp(
      view.pitch + this.pitchOffset - lookDy * PLAYER.lookRadPerPx,
      -PLAYER.pitchLimit,
      PLAYER.pitchLimit
    );
    if (lookDy !== 0) this.pitchOffset = pitch - view.pitch;
    return {
      position: view.position,
      yaw: view.yaw + this.yawOffset + this.cycles * winding,
      pitch,
      roll: 0,
      fovYDeg: CAMERA.defaultFovYDeg,
    };
  }

  get state(): TourSnapshot | null {
    if (!this.active) return null;
    return snapshot(this.active, this.clock, this.paused, this.rate);
  }

  start(id: TourId): Pose | null {
    const selected = this.routes.get(id);
    if (!selected) return null;
    this.active = selected;
    return this.restart();
  }

  restart(): Pose | null {
    if (!this.active) return null;
    this.clock = 0;
    this.cycles = 0;
    this.paused = false;
    this.yawOffset = 0;
    this.pitchOffset = 0;
    return this.pose();
  }

  togglePause(): void {
    if (!this.active) return;
    this.paused = !this.paused;
  }

  setRate(rate: number): void {
    if (Number.isFinite(rate)) this.rate = clamp(rate, 0.5, 16);
  }

  seek(seconds: number): Pose | null {
    if (!this.active || !Number.isFinite(seconds)) return this.pose();
    this.clock = clamp(seconds, 0, this.active.route.duration);
    this.cycles = 0;
    return this.pose();
  }

  stop(): void {
    this.active = null;
  }

  update(dt: number, input: Pick<InputFrame, 'lookDx' | 'lookDy'>): Pose | null {
    if (this.active && !this.paused && Number.isFinite(dt) && dt > 0) {
      if (this.active.route.closed) {
        const next = advanceLoop(this.clock, this.active.route.duration, dt * this.rate);
        this.cycles += next.cycles;
        this.clock = next.clock;
      } else this.clock = Math.min(this.clock + dt * this.rate, this.active.route.duration);
    }
    return this.pose(input);
  }
}
